import { useEffect, useMemo, useState } from "react";

import {
  getAccountingProfile,
  updateAccountingProfile,
} from "../../api/accounting";
import { buildHostUrl } from "../../config/hostRouting";
import { getRuntimeBrand } from "../../config/runtimeBrand";
import { ACCOUNTING_PRICING_PACKAGES } from "../../constants/accountingPricing";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const profileDefaults = {
  employee_beauty_professional: ["needs_personal_tax"],
  independent_professional_sole_proprietor: [
    "needs_personal_tax",
    "needs_business_tax",
    "needs_bookkeeping",
  ],
  independent_professional_incorporated: [
    "needs_personal_tax",
    "needs_corporate_tax",
    "needs_bookkeeping",
    "needs_financial_statements",
    "needs_dividends_shareholder_loans",
  ],
  salon_owner_sole_proprietor: [
    "needs_personal_tax",
    "needs_business_tax",
    "needs_gst_hst",
    "needs_payroll",
    "needs_bookkeeping",
  ],
  salon_owner_incorporated: [
    "needs_personal_tax",
    "needs_corporate_tax",
    "needs_gst_hst",
    "needs_payroll",
    "needs_bookkeeping",
    "needs_financial_statements",
    "needs_dividends_shareholder_loans",
  ],
};

function makeDefaultFlags(profileCode, moduleOptions = []) {
  const defaults = new Set(profileDefaults[profileCode] || []);
  return moduleOptions.reduce((next, option) => {
    next[option.code] = defaults.has(option.code);
    return next;
  }, {});
}

function isSalonOwnerProfile(profileCode) {
  return String(profileCode || "").startsWith("salon_owner");
}

function isIndependentProfile(profileCode) {
  return String(profileCode || "").startsWith("independent_professional");
}

function billingHref() {
  const brand = getRuntimeBrand();
  return buildHostUrl(brand?.opsHost || "ellyanna.com", "/manager/manage/billing");
}

function moneyLabel(cents) {
  const value = Number(cents || 0);
  if (!value) return "$0/month";
  return `$${Math.round(value / 100)}/month`;
}

function accountingStatusLabel(profile) {
  const currentPackage = profile?.current_accounting_package_code;
  const status = String(profile?.accounting_access_status || "").toLowerCase();
  if (currentPackage) return "Paid accounting package active";
  if (status === "trialing") {
    const days = Number(profile?.accounting_trial_days_remaining || 0);
    return days > 0 ? `${days} trial day${days === 1 ? "" : "s"} left` : "Accounting trial active";
  }
  if (status === "trial_expired") return "Accounting trial ended";
  if (status === "test_free") return "Testing account";
  return "Accounting package not selected";
}

const pricingByCode = ACCOUNTING_PRICING_PACKAGES.reduce((next, item) => {
  next[item.code] = item;
  return next;
}, {});

function getPackageByProfile(profileCode, staffCountRangeCode) {
  const profileKey = String(profileCode || "");
  const staffKey = String(staffCountRangeCode || (isSalonOwnerProfile(profileKey) ? "0_3" : "none"));
  let packageCode = null;
  if (profileKey === "independent_professional_sole_proprietor") {
    packageCode = "accounting_independent_sole";
  } else if (profileKey === "independent_professional_incorporated") {
    packageCode = "accounting_independent_corp";
  } else if (profileKey === "salon_owner_sole_proprietor" && staffKey !== "11_plus") {
    packageCode = `accounting_salon_sole_${staffKey}`;
  } else if (profileKey === "salon_owner_incorporated" && staffKey !== "11_plus") {
    packageCode = `accounting_salon_corp_${staffKey}`;
  }
  if (profileKey === "employee_beauty_professional") {
    return {
      code: null,
      label: "Included with the salon owner package",
      price_label: "$0/month",
      summary: "Employee access is for pay stubs, employment documents, and tax forms under the salon owner's SalonBusinessCPA package.",
      custom_quote: false,
    };
  }
  if (packageCode && pricingByCode[packageCode]) {
    const packageItem = pricingByCode[packageCode];
    return {
      code: packageItem.code,
      label: packageItem.label,
      price_label: `${packageItem.priceLabel}/month`,
      summary: `${packageItem.audience}. ${packageItem.range}.`,
      custom_quote: false,
    };
  }
  if (profileKey) {
    return {
      code: null,
      label: "Custom SalonBusinessCPA quote",
      price_label: "Custom quote",
      summary: "Larger salons need a quick review first because payroll, documents, GST/HST, and transaction volume can vary.",
      custom_quote: true,
    };
  }
  return null;
}

export function AccountingProfileForm({ profile, onSaved }) {
  const { token } = useAuth();
  const profileOptions = useMemo(
    () =>
      Array.isArray(profile?.client_profile_options)
        ? profile.client_profile_options
        : [],
    [profile]
  );
  const moduleOptions = useMemo(
    () => (Array.isArray(profile?.module_options) ? profile.module_options : []),
    [profile]
  );
  const staffCountOptions = useMemo(
    () =>
      Array.isArray(profile?.staff_count_range_options)
        ? profile.staff_count_range_options
        : [],
    [profile]
  );
  const [profileCode, setProfileCode] = useState(profile?.client_profile_code || "");
  const [staffCountRangeCode, setStaffCountRangeCode] = useState(
    profile?.staff_count_range_code || ""
  );
  const [moduleFlags, setModuleFlags] = useState(profile?.module_flags || {});
  const [notes, setNotes] = useState(profile?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const nextProfileCode = profile?.client_profile_code || "";
    setProfileCode(nextProfileCode);
    setStaffCountRangeCode(
      profile?.staff_count_range_code ||
        (isIndependentProfile(nextProfileCode)
          ? "none"
          : isSalonOwnerProfile(nextProfileCode)
          ? "0_3"
          : "")
    );
    setModuleFlags(profile?.module_flags || {});
    setNotes(profile?.notes || "");
  }, [profile]);

  const selectedProfile = useMemo(
    () => profileOptions.find((option) => option.code === profileCode),
    [profileCode, profileOptions]
  );

  const handleProfileChange = (nextProfileCode) => {
    setProfileCode(nextProfileCode);
    if (isIndependentProfile(nextProfileCode)) {
      setStaffCountRangeCode("none");
    } else if (isSalonOwnerProfile(nextProfileCode)) {
      setStaffCountRangeCode("0_3");
    } else {
      setStaffCountRangeCode("");
    }
    setModuleFlags(makeDefaultFlags(nextProfileCode, moduleOptions));
  };

  const toggleModule = (code) => {
    setModuleFlags((current) => ({
      ...current,
      [code]: !current?.[code],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!profileCode) {
      setError("Choose the profile that best matches your work.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await updateAccountingProfile(token, {
        client_profile_code: profileCode,
        staff_count_range_code: staffCountRangeCode || null,
        module_flags: moduleFlags,
        notes,
      });
      onSaved?.(saved);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to save your SalonBusinessCPA profile."
      );
    } finally {
      setSaving(false);
    }
  };

  const visibleStaffOptions = useMemo(() => {
    if (isIndependentProfile(profileCode)) {
      return staffCountOptions.filter((option) => option.code === "none");
    }
    if (isSalonOwnerProfile(profileCode)) {
      return staffCountOptions.filter((option) => option.code !== "none");
    }
    return [];
  }, [profileCode, staffCountOptions]);

  const recommendation = useMemo(
    () =>
      getPackageByProfile(profileCode, staffCountRangeCode) ||
      profile?.recommended_package ||
      null,
    [profileCode, profile, staffCountRangeCode]
  );
  const currentPackage = profile?.current_accounting_package_code
    ? pricingByCode[profile.current_accounting_package_code]
    : null;
  const currentPackagePriceLabel = profile?.current_accounting_package_price_cents
    ? moneyLabel(profile.current_accounting_package_price_cents)
    : "Not selected";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="grid gap-3 lg:grid-cols-5">
        {profileOptions.map((option) => {
          const active = option.code === profileCode;
          return (
            <button
              key={option.code}
              type="button"
              onClick={() => handleProfileChange(option.code)}
              className={`rounded-2xl border p-4 text-left transition ${
                active
                  ? "border-slate-950 bg-slate-950 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              }`}
            >
              <span className="block text-sm font-semibold">{option.label}</span>
              <span
                className={`mt-2 block text-xs leading-5 ${
                  active ? "text-slate-200" : "text-slate-500"
                }`}
              >
                {option.description}
              </span>
            </button>
          );
        })}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr]">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Accounting package status
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Ellyanna Booking stays separate. SalonBusinessCPA is optional and
              can be used during the accounting trial or while a paid accounting
              package is active.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Current status
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-950">
                  {accountingStatusLabel(profile)}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Current package
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-950">
                  {currentPackage?.label || "No package selected"}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {currentPackagePriceLabel}
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-600">
              Recommended package
            </div>
            {recommendation ? (
              <>
                <div className="mt-2 text-lg font-semibold text-slate-950">
                  {recommendation.label}
                </div>
                <div className="mt-1 text-sm font-semibold text-rose-700">
                  {recommendation.price_label}
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {recommendation.summary}
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Choose your profile below to see the package that best matches
                your accounting and tax needs.
              </p>
            )}
            <a
              href={billingHref()}
              className="mt-4 inline-flex min-h-10 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
            >
              Open billing options
            </a>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Services and situations
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Select the areas you want SalonBusinessCPA to help organize. We start
              with the usual items for your profile, and you can adjust them.
            </p>
          </div>
          {selectedProfile ? (
            <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
              {selectedProfile.label}
            </span>
          ) : null}
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {moduleOptions.map((option) => (
            <label
              key={option.code}
              className="flex min-h-[56px] cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
            >
              <input
                type="checkbox"
                checked={Boolean(moduleFlags?.[option.code])}
                onChange={() => toggleModule(option.code)}
                className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
              />
              <span className="font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </section>

      {visibleStaffOptions.length ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">
            Staff count for accounting
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Staff count helps estimate payroll, pay stubs, remittances, and
            record volume. Staff logins are included with the salon owner package.
          </p>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {visibleStaffOptions.map((option) => {
              const active = option.code === staffCountRangeCode;
              return (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => setStaffCountRangeCode(option.code)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    active
                      ? "border-slate-950 bg-slate-950 text-white"
                      : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <span className="block text-sm font-semibold">{option.label}</span>
                  <span className={`mt-2 block text-xs leading-5 ${active ? "text-slate-200" : "text-slate-500"}`}>
                    {option.description}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <label className="block text-sm font-semibold text-slate-950" htmlFor="accounting-profile-notes">
          Notes for your accountant
        </label>
        <textarea
          id="accounting-profile-notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={4}
          className="mt-3 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
          placeholder="Example: I have one location, I pay myself dividends, and I want help with GST/HST."
        />
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save profile"}
        </button>
        <p className="text-sm text-slate-500">
          This controls which checklists and document suggestions appear first.
        </p>
      </div>
    </form>
  );
}

export default function SalonBusinessCpaProfileIntake() {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    setError("");
    getAccountingProfile(token)
      .then((data) => {
        if (active) setProfile(data);
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail || "Unable to load your SalonBusinessCPA profile."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <SalonBusinessCpaShell
      title="Accounting setup"
      description="Confirm how you run your beauty business, what help you need, and which SalonBusinessCPA package fits your profile."
    >
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading profile...
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
          {error}
        </div>
      ) : (
        <AccountingProfileForm profile={profile} onSaved={setProfile} />
      )}
    </SalonBusinessCpaShell>
  );
}
